import type { World } from 'koota';
import type { FigureBuild } from './figure';
import { PRIM_CAPSULE, type Primitive } from './sdf/primitives';

type Pose = { tick: number; parts: Primitive[] };
type Corpse = { tick: number; pose?: Pose };
const states = new WeakMap<World, { living: Map<number, Pose>; corpses: Map<number, Corpse> }>();
function state(world: World) {
  let value = states.get(world);
  if (!value) { value = { living: new Map(), corpses: new Map() }; states.set(world, value); }
  return value;
}

export function rememberFigure(world: World, slot: number, fig: FigureBuild, tick: number): void {
  const b = fig.body;
  // Same anatomical order as RagdollParts; hands/feet merge into the terminal segments.
  state(world).living.set(slot, { tick, parts: [b[0]!, b[1]!, b[6]!, b[7]!, b[8]!, b[9]!, b[2]!, b[3]!, b[4]!, b[5]!] });
}

/** Ease out of the last living pose into the actual physics parts, without touching a body. */
export function knockoutPart(world: World, root: number, slot: number, part: number, physical: Primitive, tick: number, reducedMotion = false): Primitive {
  const s = state(world);
  let corpse = s.corpses.get(root);
  if (!corpse) {
    const last = s.living.get(slot);
    corpse = { tick, pose: last && tick >= last.tick && tick - last.tick <= 6 ? last : undefined };
    s.corpses.set(root, corpse);
  }
  const age = tick - corpse.tick;
  const previous = corpse.pose?.parts[part];
  if (reducedMotion || !previous || age < 0 || age >= 9) return physical;
  const t = Math.min(1, (age + 1) / 9), blend = 1 - (1 - t) ** 3;
  // Match the nearest endpoint orientation, avoiding an arm flipping inside out mid-blend.
  let p = previous;
  if (physical.kind === PRIM_CAPSULE) {
    const distance = (x: number, y: number) => (physical.ax - x) ** 2 + (physical.ay - y) ** 2;
    if (distance(p.bx, p.by) < distance(p.ax, p.ay)) p = { ...p, ax: p.bx, ay: p.by, bx: p.ax, by: p.ay };
  }
  const mix = (a: number, b: number) => a + (b - a) * blend;
  return { ...physical, ax: mix(p.ax, physical.ax), ay: mix(p.ay, physical.ay), bx: mix(p.bx, physical.bx), by: mix(p.by, physical.by), r: mix(p.r, physical.r) };
}

export function pruneKnockouts(world: World, roots: Set<number>): void {
  const s = states.get(world);
  if (s) for (const root of s.corpses.keys()) if (!roots.has(root)) s.corpses.delete(root);
}
